import { User } from './user.type'

// Feed Resp
export interface Feed {
  id: string
  userId: string
  userName: string
  user: User
  type: FeedType
  content: string
  status: FeedStatus
  privacy: FeedPrivacy
  likeCount: number
  commentCount: number
  shareCount: number
  viewCount: number
  sharedContent?: SharedContent
  hashtags: string[]
  createdAt: Date
  updatedAt: Date
  reactionCounts: ReactionCounts
  currentUserReaction: ReactionType
  deletedAt: Date
  mediaItems?: MediaItem[]
  latestLikers?: LatestReaction[]
}

export interface ReactionList {
  id: string
  feedId: string
  userId: string
  userName: string
  reactionType: string
  createdAt: Date
  updatedAt: Date
  user: UserLatest
}

export interface LatestReaction {
  id: string
  userId: string
  userName: string
  reactionType: string
  createdAt: CreatedAt
  user: UserLatest
}

export interface UserLatest {
  uid: string
  email?: string
  displayName: string
  imageUrl?: string
  firstName?: string
  lastName?: string
  photoURL?: string
  role: string
}

export interface ReactionCounts {
  like: number
  love: number
  haha: number
  wow: number
  sad: number
  angry: number
  total: number
}
// =========================

// Feed create post
export interface FeedCreatePostPayload {
  type: FeedType
  content: string
  mediaItems: MediaItem[]
  sharedContent: SharedContent
  privacy: FeedPrivacy
  // location: Location
  taggedUsers: TaggedUser[]
  hashtags: string[]
}

export interface Location {
  name: string
  latitude: number
  longitude: number
}

// =========================

// Feed create comment
export type FeedComment = {
  /** รหัสของ Comment */
  id: string

  /** รหัส Feed ที่แสดงความคิดเห็น */
  feedId: string

  /** รหัส Comment หลัก (ถ้าเป็น reply) */
  parentCommentId?: string

  /** รหัสผู้แสดงความคิดเห็น */
  userId: string

  /** ชื่อผู้แสดงความคิดเห็น */
  userName: string

  user: User

  /** รูปโปรไฟล์ผู้แสดงความคิดเห็น */
  userImageUrl?: string

  /** เนื้อหาความคิดเห็น */
  content: string

  /** Media ที่แนบ (รูปภาพ, GIF, Sticker) */
  media?: CommentMedia

  /** สถานะของความคิดเห็น */
  status: CommentStatus

  /** จำนวนการกด Like ในความคิดเห็น */
  likeCount: number

  /** จำนวน Reply (ถ้าเป็น comment หลัก) */
  replyCount: number

  /** แท็กผู้ใช้อื่น */
  taggedUsers?: Array<{
    userId: string
    displayName: string
  }>

  /** ข้อมูลเพิ่มเติม (JSON) */
  metadata?: string

  /** วันที่แสดงความคิดเห็น */
  createdAt: Date

  /** วันที่แก้ไขล่าสุด */
  updatedAt: Date

  /** วันที่ลบ (soft delete) */
  deletedAt?: Date

  /** รหัสผู้ลบ/แบน */
  deletedBy?: string

  /** เหตุผลในการลบ/แบน */
  deleteReason?: string
}

export interface FeedCreateCommentPayload {
  content: string
  parentCommentId: string
  media: {
    type: 'image' | 'video'
    url: string
    width?: number
    height?: number
  }
  taggedUsers: TaggedUser[]
}

export interface TaggedUser {
  userId: string
  displayName: string
}

/**
 * สถานะของความคิดเห็น
 * ACTIVE - ความคิดเห็นปกติ
 * HIDDEN - ซ่อนความคิดเห็น (โดยเจ้าของ)
 * DELETED - ลบความคิดเห็น (soft delete)
 * REPORTED - ความคิดเห็นถูกรายงาน
 * BANNED - ความคิดเห็นถูกแบน (โดย admin)
 */
export enum CommentStatus {
  ACTIVE = 'active',
  HIDDEN = 'hidden',
  DELETED = 'deleted',
  REPORTED = 'reported',
  BANNED = 'banned',
}

/**
 * ข้อมูล Media ในความคิดเห็น
 */
export interface CommentMedia {
  /** ประเภทของ media */
  type: 'image' | 'gif' | 'sticker'
  /** URL ของไฟล์ */
  url: string
  /** ความกว้าง (pixels) */
  width?: number
  /** ความสูง (pixels) */
  height?: number
}

// ==============

/**
 * ประเภทของ Feed Post
 * TEXT - โพสต์ข้อความธรรมดา
 * IMAGE - โพสต์ข้อความพร้อมรูปภาพ
 * VIDEO - โพสต์ข้อความพร้อมวิดีโอ
 * SHARED_ARTICLE - แชร์บทความ
 * SHARED_LIVESTREAM - แชร์ live stream
 * SHARED_FEED - แชร์โพสต์อื่น
 */
export enum FeedType {
  TEXT = 'text',
  IMAGE = 'image',
  VIDEO = 'video',
  SHARED_ARTICLE = 'shared_article',
  SHARED_LIVESTREAM = 'shared_livestream',
  SHARED_FEED = 'shared_feed',
}

/**
 * สถานะของ Feed Post
 * ACTIVE - โพสต์ปกติ
 * HIDDEN - ซ่อนโพสต์ (โดยเจ้าของ)
 * DELETED - ลบโพสต์ (soft delete)
 * REPORTED - โพสต์ถูกรายงาน
 * BANNED - โพสต์ถูกแบน (โดย admin)
 */
export enum FeedStatus {
  ACTIVE = 'active',
  HIDDEN = 'hidden',
  DELETED = 'deleted',
  REPORTED = 'reported',
  BANNED = 'banned',
}

/**
 * ข้อมูล Media ที่แนบในโพสต์
 */
export interface MediaItem {
  /** ประเภทของ media */
  type: 'image' | 'video'
  /** URL ของไฟล์ */
  url: string
  /** URL ของ thumbnail (สำหรับวิดีโอ) */
  thumbnailUrl?: string
  /** ขนาดไฟล์ (bytes) */
  fileSize?: number
  /** ความกว้าง (pixels) */
  width?: number
  /** ความสูง (pixels) */
  height?: number
  /** ระยะเวลา (วินาที, สำหรับวิดีโอ) */
  duration?: number
  /** คำอธิบายรูปภาพ/วิดีโอ */
  caption?: string
}

/**
 * ข้อมูลการแชร์
 */
export interface SharedContent {
  /** ประเภทเนื้อหาที่แชร์ */
  type: 'article' | 'livestream' | 'feed'
  /** ID ของเนื้อหาที่แชร์ */
  contentId: string
  /** ชื่อเรื่องของเนื้อหาที่แชร์ */
  title?: string
  /** คำอธิบายของเนื้อหาที่แชร์ */
  description?: string
  /** รูปภาพ preview */
  previewImageUrl?: string
  /** URL ของเนื้อหาต้นฉบับ */
  originalUrl?: string
  /** ข้อมูลเจ้าของเนื้อหาต้นฉบับ */
  originalAuthor?: {
    userId: string
    displayName: string
    imageUrl?: string
  }

  data?: SharedData
}

export interface SharedData {
  id: string
  userId: string
  userName: string
  firstName?: string
  lastName?: string
  userImageUrl: string
  type: string
  content: string
  mediaItems: MediaItem[]
  status: string
  privacy: string
  likeCount: number
  commentCount: number
  shareCount: number
  createdAt: CreatedAt
  role?: string
  originalContentUserRole?: string
}

export interface CreatedAt {
  _seconds: number
  _nanoseconds: number
}

// share by id
export type SharePayload = {
  shareType: ShareType
  destination: ShareDestination
  shareComment?: string
  recipientUserId?: string
  recipientUserName?: string
  externalPlatform?: string
  externalUrl?: string
  privacy?: FeedPrivacy
}

export enum ShareType {
  DIRECT = 'direct',
  WITH_COMMENT = 'with_comment',
  REPOST = 'repost',
}

export enum ShareDestination {
  FEED = 'feed',
  MESSAGE = 'message',
  EXTERNAL = 'external',
}

/**
 * ระดับความเป็นส่วนตัว
 * PUBLIC - เปิดให้ทุกคนเห็น
 * FRIENDS - เฉพาะเพื่อนเท่านั้น
 * PRIVATE - เฉพาะตัวเอง
 */
export enum FeedPrivacy {
  PUBLIC = 'public',
  FRIENDS = 'friends',
  PRIVATE = 'private',
}

// Reaction
export type ReactToFeed = {
  reactionType: ReactionType
}

export enum ReactionType {
  LIKE = 'like',
  LOVE = 'love',
  HAHA = 'haha',
  WOW = 'wow',
  SAD = 'sad',
  ANGRY = 'angry',
}
